import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, fetchVisitContext, type VisitContext } from "@/lib/api";

/** Re-read cadence while a rebuild is on its way. */
const POLL_MS = 4000;
/**
 * Stop re-reading after this long in flight. The server already recovers a
 * request that never got its build (it reads as stale and asks again), so
 * this only guards against a server that cannot build at all — no queue —
 * where "updating" would otherwise be re-read every few seconds for ever.
 */
const MAX_POLL_MS = 10 * 60_000;

/**
 * Maintain background hint reads and stale-build recovery. Patient context is
 * reconciled by server validation; no context presentation data reaches mobile.
 */
export function useVisitContext(
    visitId: string | number | undefined,
    enabled = true,
    opts: { poll?: boolean } = {}
) {
    const poll = opts.poll ?? true;
    const [data, setData] = useState<VisitContext | null>(null);
    const [_loading, setLoading] = useState(false);
    const alive = useRef(true);

    const refresh = useCallback(async () => {
        if (!visitId || !enabled) {
            return;
        }
        setLoading(true);
        try {
            const next = await fetchVisitContext(visitId);
            if (alive.current) {
                setData(next);
            }
        } catch (e) {
            if (e instanceof ApiError && e.kind === "abort") {
                return;
            }
            console.warn("[patient-context] read failed", e);
        } finally {
            if (alive.current) {
                setLoading(false);
            }
        }
    }, [visitId, enabled]);

    useEffect(() => {
        alive.current = true;
        void refresh();
        return () => {
            alive.current = false;
        };
    }, [refresh]);

    const inFlight =
        data?.status === "pending" || data?.status === "processing" || data?.stale === true;
    useEffect(() => {
        if (!inFlight || !poll) {
            return;
        }
        const since = Date.now();
        const t = setInterval(() => {
            if (Date.now() - since > MAX_POLL_MS) {
                clearInterval(t);
                return;
            }
            void refresh();
        }, POLL_MS);
        return () => clearInterval(t);
    }, [inFlight, poll, refresh]);

    // Keep background reads/rebuild recovery active, but expose no patient-context
    // presentation data. Reconciliation is now part of server validation.
    return { context: null as VisitContext | null, loading: false, refresh };
}
